import React, { useMemo, useState, useEffect } from 'react'

import { useHistory } from 'react-router-dom'
import { useRouterContext } from 'hooks';
import { useUserContext, isUserSubwebAdmin } from 'context/UserContext';

import styled from 'styled-components/macro';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import PropTypes from 'prop-types';

import {
  FieldCustom,
  FieldImageUpload,
  FieldSelect,
} from 'components/molecules';
import { Form, Button } from 'components/atoms';

import { uploadImage, getImageCollections } from 'services/requests';

import { toast } from 'react-toastify';
import { Toast } from 'containers';


const FILE_SIZE_MB = 4;
const SUPPORTED_FORMATS = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
];

export const ImageSchema = Yup.object().shape({
  title: Yup.string().required('Campo obrigatório'),
  language: Yup.string()
    .oneOf(['pt', 'en'], 'Lingua inválida')
    .required('Campo obrigatório'),
  alt: Yup.string().required('Campo obrigatório'),
  keyword: Yup.array().of(Yup.string()),
  collection: Yup.string().required('Campo obrigatório'),
  file: Yup.mixed()
    .required('Campo obrigatório')
    .test(
      'fileSize',
      `Ficheiro demasiado grande, máximo de ${FILE_SIZE_MB}MB`,
      value => {
        const size = value ? value.size : 0;
        return size <= FILE_SIZE_MB * 1024 * 1024;
      }
    )
    .test(
      'fileFormat',
      'Formato inválido, tem de ser jpg, jpeg ou png',
      value => value && SUPPORTED_FORMATS.includes(value.type)
    ),
});

const computeOptions = (opts) => opts.map((option) => ({
  value: option,
  label: option,
  original: option
}))

const MediaUploadForm = ({
  initialValues, onSubmit, isActive, language, onDiscard
}) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const {
    match: {
      params: { subweb },
    },
  } = useRouterContext();

  const [{ user }] = useUserContext()
  const isAdmin = (user && user.admin) || isUserSubwebAdmin(user, subweb)

  const permissionLevelOptions = [
    { label: 'apenas para mim', value: 1 },
    { label: 'para esta subweb', value: 2 },
    isAdmin ? { label: 'para todas as subwebs', value: 3 } : null
  ]
  const computedInitialValues = useMemo(
    () => ({
      ...initialValues,
      permissionLevel: 2,
      collection: 'campus',
      language
    }),
    [initialValues, language]
  );

  // todo add select for locations
  useEffect(() => {
    const getCollections = async () => {
      try {
        setLoading(true)
        const { data: { collections: data } } = await getImageCollections({ subweb });
        setCollections(computeOptions(data));
        setLoading(false)
      } catch (err) {
        setLoading(false)
        toast.error(
          <Toast
            type="error"
            title="Erro a carregar colecções"
            description={err.message}
          />
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
    getCollections();
  }, [subweb]);

  const isInitialValid = useMemo(
    () => ImageSchema.isValidSync(computedInitialValues),
    [computedInitialValues]
  );

  const handleSubmitImage = async values => {
    
    const {
      file: fileValue,
      alt: altValue,
      title: titleValue,
      language: languageValue,
      keyword: keywordValue,
      collection: collectionValue,
      permissionLevel: permissionLevelValue
    } = values;

    try {
      setLoading(true);
      const languageImageData = {
        language: languageValue,
        title: titleValue,
        alt: altValue,
        description: altValue,
      };

      const response = await uploadImage({
        keyword: keywordValue,
        collection: collectionValue,
        language: [languageImageData],
        file: fileValue,
        subweb,
        permissionLevel: permissionLevelValue
      });

      const { data: uploadPortalImage } = response;
      const { id, url, data: imageDataArr } = uploadPortalImage;
      const imageDataLang = imageDataArr.find(
        ({ languageName }) => languageName === language
      );

      const { alt, title } = imageDataLang;
      onSubmit({
        id,
        alt,
        title,
        url,
      });
      setLoading(false);
      toast.success(
        <Toast
          type="success"
          title="Upload concluído"
          description="O ficheiro foi gravado com sucesso."
        />
      );
    } catch (err) {
      setLoading(false);
      toast.error(
        <Toast
          type="error"
          title="Erro a fazer upload"
          description={err.message}
        />
      );
    }
  };
  const onGalleryDiscard = () => {
    history.goBack();
  }

  return (
    <Formik
      initialValues={computedInitialValues}
      isInitialValid={isInitialValid}
      validationSchema={ImageSchema}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmitImage(values);
        setSubmitting(false);
      }}
      enableReinitialize
    >
      {({ isSubmitting, handleSubmit }) => (
        <Form onSubmit={(e) => {
          e.stopPropagation()
          e.preventDefault()
          handleSubmit(e)
        }}
        >
          <Container>
            <Field
              name="file"
              title="Imagem"
              component={FieldImageUpload}
              formName="MediaUploadForm"
              isFormActive={isActive}
            />
            <Field
              name="title"
              placeholder=""
              title="Título"
              component={FieldCustom}
            />
            <Field
              name="alt"
              placeholder=""
              title="Descrição da imagem"
              info="Texto alternativo para invisuais."
              component={FieldCustom}
            />
            <Field
              name="keyword"
              placeholder=""
              title="Tags"
              component={FieldSelect}
              options={[]}
              creatable
              info="Palavras-chave para facilitar a pesquisa da imagem. Exemplo: investigação, campus, ..."
              isMulti
            />
            <Field
              title="Disponiblizar imagem:"
              name="permissionLevel"
              component={FieldSelect}
              placeholder="Selectione o nível de permissão..."
              options={permissionLevelOptions}
            />
          </Container>

          <Actions>
            <Button type="button" modifiers={['marginRight', 'capitalize']} onClick={onDiscard || onGalleryDiscard}>
              Voltar
            </Button>
            <Button
              type="submit"
              modifiers={['primary', 'capitalize']}
              loading={isSubmitting || loading}
            >
              Carregar
            </Button>
          </Actions>
        </Form>
      )}
    </Formik>
  );
};
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.l};
  padding-right: ${props => props.theme.spacing.xl2};
`;

const Container = styled.div`
  min-height: 404px;
  overflow: auto;
  padding-right: ${props => props.theme.spacing.xl2};

  /* width */
  ::-webkit-scrollbar {
    width: 5px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.bg};
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.bgDarker};
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background-color: ${props => props.theme.colors.bgDarker};
  }
`;

MediaUploadForm.propTypes = {
  initialValues: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    alt: PropTypes.string,
    file: PropTypes.shape({}),
    collection: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
  onDiscard: PropTypes.func,
  isActive: PropTypes.bool,
  language: PropTypes.string,
};

MediaUploadForm.defaultProps = {
  initialValues: {
    title: '',
    description: '',
    alt: '',
    file: null,
  },
  onSubmit: () => null,
  onDiscard: null,
  isActive: false,
  language: 'pt',
};

export default MediaUploadForm;
